class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    /* Same fallback as updateQuantity: when the mutation returns no sections,
       re-render through the GET section route instead of throwing. */
    let rendered;
    if (parsedState.sections) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        const html = parsedState.sections[section.id];
        if (!sectionElement || !html) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
      });
      rendered = Promise.resolve();
    } else {
      rendered = refreshCartDrawer().catch((error) => {
        console.error('[cart-drawer] render failed', error);
      });
    }

    rendered.then(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/*
 * Re-renders the drawer and the header count from the server.
 *
 * Deliberately uses the GET section render rather than the `sections` payload of the
 * cart mutation: on this store the POST comes back with `sections: null`, which made
 * Dawn's updateQuantity throw and leave a stale drawer behind a generic cart error.
 * The GET path returns the sections correctly.
 *
 * Exposed globally so cart-drawer-v2.js (free gift, upsell) can reuse it.
 */
function refreshCartDrawer() {
  return fetch(`${routes.cart_url}?section_id=cart-drawer`)
    .then((response) => response.text())
    .then((text) => {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const source = doc.querySelector('.drawer__inner');
      const target = document.querySelector('cart-drawer .drawer__inner');
      if (source && target) target.innerHTML = source.innerHTML;

      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', !!doc.querySelector('cart-drawer-items.is-empty'));
    })
    .then(() => fetch(`${routes.cart_url}?section_id=cart-icon-bubble`))
    .then((response) => response.text())
    .then((text) => {
      const bubble = document.getElementById('cart-icon-bubble');
      const source = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
      if (bubble && source) bubble.innerHTML = source.innerHTML;
    });
}

window.refreshCartDrawer = refreshCartDrawer;

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /* Overrides CartItems.updateQuantity so the drawer never depends on the POST's
     `sections` payload. The cart page keeps Dawn's original implementation. */
  updateQuantity(line, quantity, name, variantId) {
    /*
     * CartItems caches this in its constructor. After a drawer re-render the cached
     * node is detached (or was never found), and enableLoading() touches it
     * synchronously before the fetch — so a stale reference silently kills the whole
     * interaction instead of surfacing. Re-resolve it against the live DOM first.
     */
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    try {
      this.enableLoading(line);
    } catch (error) {
      console.error('[cart-drawer] enableLoading failed', error);
    }

    const body = JSON.stringify({ line, quantity });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        /* Shopify answers a rejected mutation with {status, message, description}. */
        if (parsedState.status) {
          this.updateLiveRegions(line, parsedState.description || window.cartStrings.error);
          return null;
        }
        if (parsedState.errors) {
          this.updateLiveRegions(line, parsedState.errors);
          return null;
        }
        return refreshCartDrawer().then(() => parsedState);
      })
      .then((parsedState) => {
        if (!parsedState) return;

        this.updateLiveRegions(line, '');
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch((error) => {
        console.error('[cart-drawer] update failed', error);
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        /* Never let cleanup on a replaced DOM turn into an unhandled rejection. */
        try {
          this.disableLoading(line);
        } catch (error) {
          console.error('[cart-drawer] disableLoading failed', error);
        }
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/*
 * At the minimum quantity Dawn just dims the minus button, leaving the bin icon as the
 * only way out. In the drawer, make that last press remove the line instead.
 *
 * Capture phase on the document: it runs before quantity-input's own click handler and
 * stops the event reaching it, so the input never tries to step below its minimum. It
 * also survives every drawer re-render, unlike a listener bound to the button itself.
 */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const button = target.closest('cart-drawer .cart-item__quantity .quantity__button[name="minus"]');
    if (!button) return;

    const wrapper = button.closest('quantity-input');
    const input = wrapper && wrapper.querySelector('.quantity__input');
    if (!input) return;

    const value = parseInt(input.value, 10);
    const min = parseInt(input.min, 10) || 1;
    if (Number.isNaN(value) || value > min) return;

    event.preventDefault();
    event.stopPropagation();

    const items = button.closest('cart-drawer-items');
    if (items) items.updateQuantity(input.dataset.index, 0);
  },
  true
);
